import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/context/SiteContext";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { AdminBanner } from "@/components/site/Admin";
import { useSeo } from "@/lib/useSeo";
import Home from "@/pages/Home";

const Donate = lazy(() => import("@/pages/Donate"));
const ToyDrive = lazy(() => import("@/pages/ToyDrive"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminImages = lazy(() => import("@/pages/AdminImages"));
const AdminInbox = lazy(() => import("@/pages/AdminInbox"));
const Shirts = lazy(() => import("@/pages/Shirts"));
const About = lazy(() => import("@/pages/About"));
const GetInvolved = lazy(() => import("@/pages/GetInvolved"));
const Contact = lazy(() => import("@/pages/Contact"));

const skipToMain = (e) => {
  e.preventDefault();
  const main = document.getElementById("main");
  if (!main) return;
  main.setAttribute("tabindex", "-1");
  main.focus();
  main.scrollIntoView();
};

const PageFallback = () => <main id="main" className="min-h-screen" aria-busy="true" />;

const PAGE_KEYS = { "/": "home", "/toy-drive": "toy-drive", "/donate": "donate", "/shirts-for-olivia": "shirts", "/about": "about", "/get-involved": "get-involved", "/contact": "contact" };

function Shell() {
  const { pathname, hash } = useLocation();
  const page = PAGE_KEYS[pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname] || "other";
  useSeo();
  useEffect(() => {
    if (!hash || hash === "#main") {
      window.scrollTo(0, 0);
      return undefined;
    }
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++tries > 20) clearInterval(timer);
      el?.scrollIntoView();
    }, 100);
    return () => clearInterval(timer);
  }, [pathname, hash]);
  return (
    <div className="of-page" data-page={page} data-testid="page-theme">
      <a href="#main" onClick={skipToMain} data-testid="skip-to-main-link"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:inline-flex focus:min-h-[44px] focus:items-center focus:rounded-full focus:bg-white focus:px-5 focus:font-semibold focus:text-[#1F2328] focus:shadow-lg">
        Skip to main content
      </a>
      <div className="sticky top-0 z-50">
        <AdminBanner />
        <Header stripped={pathname === "/donate"} />
      </div>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/toy-drive" element={<ToyDrive />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/inbox" element={<AdminInbox />} />
          <Route path="/admin/images" element={<AdminImages />} />
          <Route path="/shirts-for-olivia" element={<Shirts />} />
          <Route path="/about" element={<About />} />
          <Route path="/get-involved" element={<GetInvolved />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<ComingSoon notFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <Shell />
        <Toaster position="bottom-right" richColors />
      </SiteProvider>
    </BrowserRouter>
  );
}
